<template>
    <div class="max-w-4xl mx-auto p-6  mt-10 space-y-8">

        <!-- Titre -->
        <h1 class="text-2xl font-bold">Modifier l'entreprise</h1>

        <div v-if="entrepriseId" class="bg-gray-100 p-4 rounded shadow space-y-2">
            <p class="text-sm text-gray-600">Lien public de la page vitrine :</p>

            <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
                <!-- Champ texte modifiable pour le slug -->
                <input v-model="entreprise.slug" type="text" placeholder="Entrez un slug unique"
                    class="w-full sm:flex-1 border border-gray-300 rounded px-3 py-2 text-gray-800"
                    @input="resetSlugStatus" />

                <!-- Bouton Vérifier -->
                <button @click="checkSlug" :disabled="!isSlugValid"
                    class="bg-primary text-white px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
                    Vérifier
                </button>
            </div>

            <!-- Message de statut -->
            <p v-if="slugStatus === 'available'" class="text-green-600">URL disponible ✔️</p>
            <p v-else-if="slugStatus === 'unavailable'" class="text-red-600">URL déjà pris ❌</p>
            <p v-else-if="slugStatus === 'invalid'" class="text-red-600">Format invalide (lettres, chiffres, tirets
                uniquement)</p>

            <!-- Affichage du lien public avec le slug -->
            <span v-if="abonnement">
                <a :href="`https://www.myprofolia.com/${entreprise.slug || `entreprise-${entrepriseId}`}`"
                    target="_blank" class="text-primary hover:underline break-all">
                    https://www.myprofolia.com/{{ entreprise.slug || `entreprise-${entrepriseId}` }}
                </a>
            </span>
            <span v-else class="text-gray-500 break-all">
                https://www.myprofolia.com/{{ entreprise.slug || `entreprise-${entrepriseId}` }}
            </span>
        </div>

        <!-- Formulaire -->
        <form @submit.prevent="submitForm" class="space-y-10">

            <!-- SECTION 1 : Informations générales -->
            <div class="p-6 bg-white rounded-xl shadow-md space-y-4">
                <h2 class="text-lg font-bold text-gray-800 border-b pb-2">Informations générales</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block font-semibold">Nom de l'entreprise</label>
                        <input v-model="entreprise.nom" type="text" class="w-full border rounded p-2" />
                    </div>

                    <div>
                        <label class="block font-semibold">Activité</label>
                        <input v-model="entreprise.activite" type="text" class="w-full border rounded p-2" />
                    </div>

                    <div>
                        <label class="block font-semibold">Zone d'activité</label>
                        <input v-model="entreprise.zone" type="text" class="w-full border rounded p-2"
                            placeholder="Ex : Paris et alentours" />
                    </div>

                    <div>
                        <label class="block font-semibold">Téléphone</label>
                        <input v-model="entreprise.telephone" type="text" class="w-full border rounded p-2" />
                    </div>

                    <div>
                        <label class="block font-semibold">Email</label>
                        <input v-model="entreprise.email" type="email" class="w-full border rounded p-2" />
                    </div>

                    <div class="md:col-span-2">
                        <label class="block font-semibold">Description</label>
                        <textarea v-model="entreprise.description" rows="4"
                            class="w-full border rounded p-2"></textarea>
                    </div>

                    <div class="md:col-span-2">
                        <label class="block font-semibold">Adresse complète</label>
                        <input v-model="entreprise.adresse" type="text" class="w-full border rounded p-2 mb-2"
                            placeholder="Adresse (ex : 12 rue du Général)" />
                        <div class="flex gap-4">
                            <input v-model="entreprise.code_postal" type="text" maxlength="10"
                                class="w-1/3 border rounded p-2" placeholder="Code postal" />
                            <input v-model="entreprise.ville" type="text" class="w-2/3 border rounded p-2"
                                placeholder="Ville" />
                        </div>
                    </div>

                    <div class="md:col-span-2 p-4 bg-violet-50 border border-violet-200 rounded text-sm text-gray-700">
                        <p class="font-semibold mb-1">💡 Avis Google :</p>
                        <ul class="list-disc pl-5 space-y-1">
                            <li>Rendez-vous sur votre page Google Maps.</li>
                            <li>Cliquez sur "Partager" puis copiez le lien fourni.</li>
                            <li>Collez ce lien dans le champ "Lien Google Maps" pour afficher vos avis.</li>
                        </ul>
                    </div>

                    <div class="md:col-span-2">
                        <label class="block font-semibold">Lien Google Maps</label>
                        <input v-model="googleReview.google_map_url" type="text" class="w-full border rounded p-2"
                            placeholder="https://maps.app.goo.gl/..." />
                    </div>
                </div>
            </div>

            <!-- SECTION 2 : Logo et Couleur -->
            <div class="p-6 bg-white rounded-xl shadow-md space-y-4">
                <h2 class="text-lg font-bold text-gray-800 border-b pb-2">Logo et Couleur</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block font-semibold mb-1">Logo (fichier uniquement)</label>
                        <div v-if="selectedFile">
                            <img :src="previewUrl" alt="Aperçu logo" class="w-32 h-32 object-contain mb-2" />
                        </div>
                        <div v-else-if="entreprise && entreprise.logo_url">
                            <img :src="fullLogoUrl" alt="Logo existant"
                                class="w-32 h-32 object-contain mb-2 rounded border border-gray-300" />
                        </div>
                        <input type="file" @change="handleFileChange" accept="image/*" class="w-full" />
                        <p class="text-sm text-gray-500 mt-1">Taille max : 5 Mo. Formats : JPG, JPEG, PNG.</p>
                        <p v-if="uploadError" class="text-sm text-red-500 mt-1">{{ uploadError }}</p>
                    </div>

                    <div>
                        <label class="block font-semibold mb-1">Couleur principale</label>
                        <input v-model="entreprise.couleur" type="text"
                            class="w-full border rounded p-2 placeholder-gray-400" placeholder="#ff5733" />
                        <p class="text-sm text-gray-500 mt-1">Exemple : #ff5733</p>
                        <input v-model="entreprise.couleur" type="color" class="w-12 h-12 p-0 border rounded mt-2" />
                    </div>
                </div>
            </div>

            <!-- SECTION 3 : Réservation / Devis -->
            <div class="p-6 bg-white rounded-xl shadow-md space-y-4">
                <h2 class="text-lg font-bold text-gray-800 border-b pb-2">Réservation et devis</h2>
                <div class="md:col-span-2 p-4 bg-violet-50 border border-violet-200 rounded text-sm text-gray-700">
                    <p class="font-semibold mb-1">💡 Fonctionnement de la réservation :</p>
                    <ul class="list-disc pl-5 space-y-1">
                        <li>Si "Afficher une section devis" n’est pas coché et qu’aucun lien de réservation →
                            coordonnées affichées.</li>
                        <li>Si "Afficher une section devis" n’est pas coché et qu’un lien de réservation est renseigné →
                            bouton de réservation.</li>
                        <li>Si "Afficher une section devis" est coché → section spéciale pour demander un devis.</li>
                    </ul>
                </div>
                <div>
                    <label class="block font-semibold">Lien plateforme de réservation</label>
                    <input v-model="entreprise.reservation_url" type="text" class="w-full border rounded p-2"
                        placeholder="https://www.planity.com/..." />
                </div>
                <div class="flex items-center gap-2 mt-2">
                    <input id="afficher_devis" type="checkbox" v-model="entreprise.afficher_devis"
                        class="rounded border-gray-300" />
                    <label for="afficher_devis" class="text-gray-700">Afficher une section devis (si pas de
                        réservation)</label>
                </div>
            </div>

            <!-- SECTION 4 : Site Web et Réseaux sociaux -->
            <div class="p-6 bg-white rounded-xl shadow-md space-y-4">
                <h2 class="text-lg font-bold text-gray-800 border-b pb-2">Site Web et Réseaux sociaux</h2>
                <div>
                    <label class="block font-semibold mb-1">Site Web</label>
                    <input v-model="entreprise.siteweb" type="text" placeholder="https://www..."
                        class="w-full border rounded p-2" />
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <input v-model="reseaux.facebook" type="text" placeholder="Lien Facebook"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.instagram" type="text" placeholder="Lien Instagram"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.youtube" type="text" placeholder="Lien YouTube"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.linkedin" type="text" placeholder="Lien LinkedIn"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.tiktok" type="text" placeholder="Lien TikTok"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.snapchat" type="text" placeholder="Lien Snapchat"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.whatsapp" type="text" placeholder="Lien WhatsApp"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.github" type="text" placeholder="Lien GitHub"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.spotify" type="text" placeholder="Lien Spotify"
                        class="w-full border rounded p-2" />
                    <input v-model="reseaux.twitter" type="text" placeholder="Lien Twitter - X"
                        class="w-full border rounded p-2" />
                </div>
            </div>

            <!-- SECTION 4 - Horaires -->
            <div class="p-6 bg-white rounded-xl shadow-md space-y-4">
                <h2 class="text-xl font-bold mb-4">Horaires d'ouverture</h2>
                <div class="mb-6 p-4 bg-violet-50 border border-violet-200 rounded text-sm text-gray-700">
                    <p class="font-semibold mb-1">💡 Fonctionnement :</p>
                    <ul class="list-disc pl-5 space-y-1">
                        <li>Si un jour est coché <strong>ouvert</strong> sans horaires → affichage : <em>Ouvert</em>
                        </li>
                        <li>Si seule l'heure d'ouverture matin et l'heure de fermeture après-midi sont remplies →
                            affichage : <em>09:00 - 18:00</em></li>
                        <li>Si toutes les plages horaires sont remplies → affichage : <em>09:00 - 12:00, 14:00 -
                                18:00</em></li>
                    </ul>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div v-for="(jour, index) in jours" :key="index" class="mb-4 border p-4 rounded">
                        <div class="flex justify-between items-center">
                            <label class="font-semibold">{{ jour }}</label>
                            <button type="button" @click="resetHoraire(jour)"
                                class="text-sm text-violet-500 hover:underline">Effacer</button>
                        </div>

                        <div class="flex items-center gap-4 mt-2">
                            <label>
                                <input type="checkbox" v-model="horaires[jour].ouvert" />
                                Ouvert
                            </label>
                        </div>

                        <div v-if="horaires[jour].ouvert" class="grid grid-cols-2 gap-4 mt-2">
                            <div>
                                <label>Matin</label>
                                <input type="time" v-model="horaires[jour].ouverture_matin"
                                    class="border rounded p-2 w-full" />
                                <input type="time" v-model="horaires[jour].fermeture_matin"
                                    class="border rounded p-2 w-full mt-1" />
                            </div>
                            <div>
                                <label>Après-midi</label>
                                <input type="time" v-model="horaires[jour].ouverture_apresmidi"
                                    class="border rounded p-2 w-full" />
                                <input type="time" v-model="horaires[jour].fermeture_apresmidi"
                                    class="border rounded p-2 w-full mt-1" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- SECTION - Prestations -->
            <div class="p-6 bg-white rounded-xl shadow-md space-y-4">
                <h2 class="text-xl font-bold mb-4">Prestations & Tarifs</h2>

                <div class="mb-6 p-4 bg-violet-50 border border-violet-200 rounded text-sm text-gray-700">
                    <p class="font-semibold mb-1">💡 Fonctionnement :</p>
                    <ul class="list-disc pl-5 space-y-1">
                        <li>Ajoutez vos prestations avec un titre et un prix.</li>
                        <li>Vous pouvez ajouter plusieurs lignes.</li>
                        <li>Ces prestations seront affichées automatiquement sur votre site vitrine.</li>
                    </ul>
                </div>

                <div class="space-y-4">
                    <div v-for="(prestation, index) in prestations" :key="index"
                        class="border p-4 rounded bg-white shadow-sm">
                        <div class="flex justify-between items-center mb-3">
                            <label class="font-semibold">Prestation {{ index + 1 }}</label>
                            <button type="button" @click="removePrestation(index)"
                                class="text-sm text-primary hover:underline">
                                Supprimer
                            </button>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label class="block text-sm font-medium mb-1">Titre</label>
                                <input type="text" v-model="prestation.titre" placeholder="Ex : Coupe homme"
                                    class="border rounded p-2 w-full" />
                            </div>
                            <div>
                                <label class="block text-sm font-medium mb-1">Prix (€)</label>
                                <input type="number" step="0.01" v-model="prestation.prix" placeholder="Ex : 20"
                                    class="border rounded p-2 w-full" />
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Description</label>
                            <input type="text" v-model="prestation.description"
                                placeholder="Ex : Description du service" class="border rounded p-2 w-full" />
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <button type="button" @click="addPrestation"
                        class="bg-violet-500 text-white px-4 py-2 rounded hover:bg-violet-600">
                        + Ajouter une prestation
                    </button>
                </div>
            </div>


            <div class="pt-6 flex space-x-4">
                <!-- Bouton de soumission -->
                <button type="submit"
                    class="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark transition duration-200">
                    Publier
                </button>

                <!-- Bouton de preview visible uniquement si non abonné -->
                <button v-if="!abonnement" type="button" @click="previewSubmit"
                    class="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark transition duration-200">
                    Prévisualiser
                </button>
            </div>
            <div v-if="!abonnement">
                <p class="px-6 text-gray-500 italic text-sm">Si vous ne parvenez pas à prévisualiser votre page, c'est
                    peut être que votre navigateur
                    bloque les
                    pop-up. Rendez-vous dans la <a href="/faq" class="underline text-primary">faq</a> pour plus
                    d'informations</p>
            </div>

        </form>
    </div>

    <!-- Modal validation -->
    <div v-if="showSuccess" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div class="bg-white rounded p-8 max-w-sm w-full text-center shadow-lg" role="alert" aria-live="assertive">
            <h2 class="text-2xl font-bold mb-4 text-primary">Enregistré !</h2>
            <p class="mb-6 text-gray-700">
                Vos changements ont été enregistrés avec succès.
            </p>
            <button @click="showSuccess = false"
                class="bg-primary text-white px-4 py-2 rounded hover:bg-hover transition duration-200">
                Fermer
            </button>
        </div>
    </div>

    <!-- Modal erreur -->
    <div v-if="showError" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div class="bg-white rounded p-8 max-w-sm w-full text-center shadow-lg" role="alert" aria-live="assertive">
            <h2 class="text-2xl font-bold mb-4 text-red-600">Erreur</h2>
            <p class="mb-6 text-gray-700">
                {{ errorMessage }}
            </p>
            <button @click="showError = false"
                class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition duration-200">
                Fermer
            </button>
        </div>
    </div>

</template>


<script setup>
import { ref, onMounted, computed, reactive } from 'vue'

const token = localStorage.getItem('token')

const user = ref(null); // stocke les infos user

const abonnement = computed(() => {
    if (!user.value) return false;
    const abo = user.value.abonnement;
    return abo === true || abo === 't' || abo === 1 || abo === 'true';
});

// URL de base pour les appels à l'API backend (adapter selon l'environnement)
const baseUrl = process.env.VUE_APP_BASE_URL

// Référence pour stocker l'ID de l'entreprise de l'utilisateur connecté
const entrepriseId = ref(null);

// Objet réactif stockant les infos de l'entreprise avec valeurs par défaut vides
const entreprise = ref({
    nom: '',
    activite: '',
    logo_url: '',
    adresse: '',
    code_postal: '',
    ville: '',
    zone: '',
    telephone: '',
    email: '',
    siteweb: '',
    reservation_url: '',
    description: '',
    couleur: '',
    slug: '',
    afficher_devis: false
})

// Objet réactif stockant les URLs des réseaux sociaux avec clés initialisées à chaîne vide
const reseaux = ref({
    facebook: '',
    instagram: '',
    youtube: '',
    linkedin: '',
    tiktok: '',
    snapchat: '',
    whatsapp: '',
    github: '',
    spotify: '',
    twitter: ''
})

const googleReview = ref({
    google_map_url: '',
    rating: '',
    total_reviews: '',
    modified_at: ''
})

// Liste des jours de la semaine utilisée pour itérations et gestion des horaires
const jours = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

// Objet réactif pour stocker les horaires d'ouverture par jour, initialisé avec des valeurs par défaut
const horaires = reactive({})
for (const jour of jours) {
    horaires[jour] = {
        ouvert: false,
        ouverture_matin: '',
        fermeture_matin: '',
        ouverture_apresmidi: '',
        fermeture_apresmidi: ''
    }
}

const prestations = ref([]);


const addPrestation = () => {
    prestations.value.push({ titre: "", description: "", prix: "" });
};

const removePrestation = (index) => {
    prestations.value.splice(index, 1);
};

// Référence pour afficher le modal de succès après enregistrement
// ref d'erreurs
const showSuccess = ref(false);
const showError = ref(false);
const errorMessage = ref("");
const uploadError = ref("")


// Variables pour la gestion de la prévisualisation du logo lors du chargement d'un nouveau fichier
const selectedFile = ref(null)  // fichier sélectionné dans l'input type=file
const previewUrl = ref('')      // URL temporaire pour afficher l'aperçu de l'image

const slugStatus = ref(null)

const slugRegex = /^[a-zA-Z0-9-]+$/

const isSlugValid = computed(() => {
    return entreprise.value.slug?.length > 0 && slugRegex.test(entreprise.value.slug)
})

function resetSlugStatus() {
    slugStatus.value = null
}


async function checkSlug() {
    const slug = (entreprise.value.slug?.trim() || '').toLowerCase();

    if (!slugRegex.test(slug)) {
        slugStatus.value = 'invalid';
        return 'invalid';
    }

    try {
        const response = await fetch(`${baseUrl}/api/entreprises/check-slug?slug=${encodeURIComponent(slug)}&id=${entreprise.value.id}`)

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.available) {
            slugStatus.value = 'available';
            return 'available';
        } else {
            slugStatus.value = 'unavailable';
            return 'unavailable';
        }
    } catch (error) {
        console.error('Erreur lors de la vérification du slug:', error);
        slugStatus.value = 'unavailable'; // ou 'error' si tu préfères une autre gestion
        return 'unavailable';
    }
}



/**
 * Gère le changement de fichier dans l'input et crée une URL locale pour prévisualisation
 * @param {Event} event - événement du changement d'input
 */

function handleFileChange(event) {
    uploadError.value = ""
    const file = event.target.files[0]

    if (!file) {
        selectedFile.value = null
        previewUrl.value = null
        return
    }

    // Vérif taille max (5 Mo)
    if (file.size > 5 * 1024 * 1024) {
        uploadError.value = "Le fichier est trop lourd (max 5 Mo)."
        selectedFile.value = null
        previewUrl.value = null
        return
    }

    // Vérif dimensions max (1024x1024px)
    const img = new Image()
    img.onload = () => {
        if (img.width > 1024 || img.height > 1024) {
            uploadError.value = "L'image est trop grande (max 1024x1024px)."
            selectedFile.value = null
            previewUrl.value = null
            return
        }

        // ✅ OK → on affiche la preview
        selectedFile.value = file
        previewUrl.value = URL.createObjectURL(file)
    }
    img.src = URL.createObjectURL(file)
}


/**
 * Calcul de l'URL absolue du logo pour l'affichage :
 * Si logo_url commence par "http", on utilise tel quel,
 * sinon on ajoute la baseUrl devant.
 */
const fullLogoUrl = computed(() => {
    if (!entreprise.value || !entreprise.value.logo_url) return ''
    return entreprise.value.logo_url.startsWith('http')
        ? entreprise.value.logo_url
        : `${baseUrl}${entreprise.value.logo_url.startsWith('/') ? '' : '/'}${entreprise.value.logo_url}`
})

/**
 * Réinitialise les horaires d'un jour donné (utile pour formulaire)
 * @param {string} jour - jour de la semaine (ex: "Lundi")
 */
function resetHoraire(jour) {
    horaires[jour].ouverture_matin = ''
    horaires[jour].fermeture_matin = ''
    horaires[jour].ouverture_apresmidi = ''
    horaires[jour].fermeture_apresmidi = ''
}

/**
 * Coupe les secondes d'une chaîne horaire "HH:MM:SS" en "HH:MM"
 * @param {string} time - horaire au format "HH:MM:SS"
 * @returns {string} - horaire formaté "HH:MM"
 */
const trimSeconds = (time) => {
    if (!time) return ''
    return time.length === 8 ? time.slice(0, 5) : time
}



/**
 * Fonction asynchrone pour récupérer l'entreprise de l'utilisateur connecté
 * Utilise l'API backend pour obtenir les données de l'entreprise
 */
async function fetchEntrepriseId() {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}/api/entreprises/user`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });

        if (!response.ok) {
            throw new Error(`Erreur HTTP ${response.status}`);
        }

        const data = await response.json();
        entrepriseId.value = data.id;
    } catch (error) {
        console.error('Erreur lors de la récupération de l’ID entreprise :', error);
    }
}

/**
 * Fonction asynchrone pour charger les données user via l'API backend
 */
const fetchUser = async () => {
    try {
        const res = await fetch(`${baseUrl}/api/users/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
            user.value = await res.json();
        } else {
            console.error('Impossible de récupérer les infos utilisateur');
            // Optionnel : gérer la déconnexion ou redirection vers login
        }
    } catch (error) {
        console.error('Erreur API user/me:', error);
    }
};


/**
 * Fonction asynchrone pour charger les données entreprise, réseaux sociaux et horaires via l'API backend
 */
const fetchData = async () => {
    try {

        const headers = {
            'Authorization': `Bearer ${token}`
        };
        // Appels parallèles pour optimiser le chargement
        const [res1, res2, res3, res4, res5] = await Promise.all([
            fetch(`${baseUrl}/api/entreprises/${entrepriseId.value}`, { headers }),
            fetch(`${baseUrl}/api/reseaux/${entrepriseId.value}`, { headers }),
            fetch(`${baseUrl}/api/horaires/${entrepriseId.value}`, { headers }),
            fetch(`${baseUrl}/api/prestations/${entrepriseId.value}`, { headers }),
            fetch(`${baseUrl}/api/google-review/${entrepriseId.value}`, { headers }),
        ])

        // Vérification des réponses
        if (!res1.ok || !res2.ok || !res3.ok || !res4.ok || !res5.ok) throw new Error("Erreur lors du chargement")

        // Récupération des données JSON
        entreprise.value = await res1.json()
        reseaux.value = await res2.json()
        const horairesData = await res3.json()
        prestations.value = await res4.json()
        googleReview.value = await res5.json()

        // Transformation du tableau d'horaires en objet indexé par jour avec majuscule au début
        const horairesMap = {}
        for (const item of horairesData) {
            const jour = item.jour.charAt(0).toUpperCase() + item.jour.slice(1).toLowerCase()
            horairesMap[jour] = {
                ouvert: item.ouvert === true || item.ouvert === 'true',
                ouverture_matin: trimSeconds(item.ouverture_matin),
                fermeture_matin: trimSeconds(item.fermeture_matin),
                ouverture_apresmidi: trimSeconds(item.ouverture_apresmidi),
                fermeture_apresmidi: trimSeconds(item.fermeture_apresmidi)
            }
        }

        // Mise à jour des horaires réactifs sans casser la réactivité de Vue
        for (const jour of jours) {
            if (horairesMap[jour]) {
                Object.assign(horaires[jour], horairesMap[jour])
            } else {
                // Si pas d'horaires reçus pour le jour, on réinitialise à fermé
                Object.assign(horaires[jour], {
                    ouvert: false,
                    ouverture_matin: '',
                    fermeture_matin: '',
                    ouverture_apresmidi: '',
                    fermeture_apresmidi: ''
                })
            }
        }
    } catch (err) {
        // Gestion simple des erreurs de chargement
        console.error('Erreur lors du chargement :', err)
    }
}

/**
 * Fonction asynchrone pour envoyer les modifications au backend via API
 * Effectue les mises à jour de l'entreprise, des réseaux sociaux et des horaires
 */
const submitForm = async () => {

    showSuccess.value = false;
    showError.value = false;
    errorMessage.value = "";
    const tempSlug = entreprise.value.slug; // sauvegarde temporaire du slug avant validation

    try {
        //Bloc de traitement cas avec abonnement
        /* if (!abonnement.value) {
            entreprise.value.slug = null;
        } else {
            const slugResult = await checkSlug();
            if (slugResult !== 'available') {
                errorMessage.value = "Le slug est invalide ou déjà utilisé.";
                showError.value = true;
                return;
            }
        } */

        // Vérification du slug (pour tous les utilisateurs)
        const slugResult = await checkSlug();
        if (slugResult !== 'available') {
            errorMessage.value = "Le slug est invalide ou déjà utilisé.";
            showError.value = true;
            return;
        }

        // Préparation des données de l'entreprise dans un FormData (pour gestion fichier logo)
        const formData = new FormData()
        for (const key in entreprise.value) {
            const value = entreprise.value[key]
            if (value !== null && value !== undefined) {
                formData.append(key, value)
            }
        }

        // Ajout du fichier logo si sélectionné
        if (selectedFile.value) {
            formData.append('logo', selectedFile.value)
        }

        // Envoi de la mise à jour de l'entreprise (PUT)
        const res = await fetch(`${baseUrl}/api/entreprises/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!res.ok) {
            const errorMsg = await res.text();
            throw new Error(errorMsg || 'Erreur mise à jour entreprise');
        }

        // Envoi de la mise à jour des réseaux sociaux (JSON)
        const res2 = await fetch(`${baseUrl}/api/reseaux/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(reseaux.value)
        });

        if (!res2.ok) {
            const errorMsg = await res2.text();
            throw new Error(errorMsg || 'Erreur mise à jour réseaux sociaux');
        }

        // Préparation et envoi des horaires mis à jour
        const horairesPayload = {}
        for (const jour of jours) {
            horairesPayload[jour] = { ...horaires[jour] }
        }

        const res3 = await fetch(`${baseUrl}/api/horaires/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(horairesPayload)
        });


        if (!res3.ok) {
            const errorMsg = await res3.text();
            throw new Error(errorMsg || 'Erreur mise à jour horaires');
        }

        // Préparation et envoi des prestations
        const res4 = await fetch(`${baseUrl}/api/prestations/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(
                prestations.value.map(p => ({
                    ...p,
                    prix: p.prix === "" ? null : p.prix
                }))
            )
        });

        if (!res4.ok) {
            const errorMsg = await res4.text();
            throw new Error(errorMsg || 'Erreur mise à jour prestations');
        }

        const res5 = await fetch(`${baseUrl}/api/google-review/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ google_map_url: googleReview.value.google_map_url })
        });


        if (!res5.ok) {
            const errorMsg = await res5.text();
            throw new Error(errorMsg || 'Erreur mise à jour google review');
        }

        // Si pas abonné, rediriger vers la page d'abonnement
        /* if (!abonnement.value) {
            window.location.href = '/subscribe';
            return;
        } */

        // Succès
        showSuccess.value = true;

        // Réinitialisation du slug temporaire après succès
        entreprise.value.slug = tempSlug;

        window.open(`/${entreprise.value.slug}`, '_blank');



    } catch (err) {
        console.error('Erreur lors de la soumission :', err)
        errorMessage.value = err.message || "Erreur lors de l'enregistrement";
        showError.value = true;
    }
}

/**
 * Fonction asynchrone pour envoyer les modifications au backend via API
 * Effectue les mises à jour de l'entreprise, des réseaux sociaux et des horaires
 * Affiche la page de prévisualisation si l'utilisateur n'est pas abonné
 */
const previewSubmit = async () => {

    showSuccess.value = false;
    showError.value = false;
    errorMessage.value = "";
    const tempSlug = entreprise.value.slug; // sauvegarde temporaire du slug avant validation


    try {

        entreprise.value.slug = null;

        // Préparation des données de l'entreprise dans un FormData (pour gestion fichier logo)
        const formData = new FormData()
        for (const key in entreprise.value) {
            const value = entreprise.value[key]
            if (value !== null && value !== undefined) {
                formData.append(key, value)
            }
        }

        // Ajout du fichier logo si sélectionné
        if (selectedFile.value) {
            formData.append('logo', selectedFile.value)
        }

        // Envoi de la mise à jour de l'entreprise (PUT)
        const res = await fetch(`${baseUrl}/api/entreprises/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!res.ok) {
            const errorMsg = await res.text();
            throw new Error(errorMsg || 'Erreur mise à jour entreprise');
        }

        // Envoi de la mise à jour des réseaux sociaux (JSON)
        const res2 = await fetch(`${baseUrl}/api/reseaux/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(reseaux.value)
        });

        if (!res2.ok) {
            const errorMsg = await res2.text();
            throw new Error(errorMsg || 'Erreur mise à jour réseaux sociaux');
        }

        // Préparation et envoi des horaires mis à jour
        const horairesPayload = {}
        for (const jour of jours) {
            horairesPayload[jour] = { ...horaires[jour] }
        }

        const res3 = await fetch(`${baseUrl}/api/horaires/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(horairesPayload)
        });


        if (!res3.ok) {
            const errorMsg = await res3.text();
            throw new Error(errorMsg || 'Erreur mise à jour horaires');
        }

        // Préparation et envoi des prestations
        const res4 = await fetch(`${baseUrl}/api/prestations/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(
                prestations.value.map(p => ({
                    ...p,
                    prix: p.prix === "" ? null : p.prix
                }))
            )
        });

        if (!res4.ok) {
            const errorMsg = await res4.text();
            throw new Error(errorMsg || 'Erreur mise à jour prestations');
        }

        const res5 = await fetch(`${baseUrl}/api/google-review/${entrepriseId.value}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ google_map_url: googleReview.value.google_map_url })
        });

        if (!res5.ok) {
            const errorMsg = await res5.text();
            throw new Error(errorMsg || 'Erreur mise à jour google review');
        }

        // Réinitialisation du slug temporaire après succès
        entreprise.value.slug = tempSlug;


        window.open('/preview', '_blank');

    } catch (err) {
        console.error('Erreur lors de la soumission :', err)
        errorMessage.value = err.message || "Erreur lors de l'enregistrement";
        showError.value = true;
    }
}

// Chargement initial des données à l'affichage du composant
onMounted(async () => {
    await fetchUser();
    await fetchEntrepriseId();
    await fetchData();

    const urlParams = new URLSearchParams(window.location.search);
    const isPaymentSuccess = urlParams.get("payment") === "success";
    if (isPaymentSuccess && user.value?.abonnement === true) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "subscription_success",
            userId: user.value.id,
            customerId: user.value.stripecustomerid,
            subscriptionId: user.value.stripesubscriptionid,
            planType: user.value.planType,   // déjà calculé par le backend
            value: user.value.value,         // idem
            currency: user.value.currency,   // "EUR"
            predicted_ltv: user.value.planType === 'annuel' ? 59.99 : 12 * user.value.value
        });
    }

    // Nettoyer l’URL
    const newUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
});

</script>
